import {
  Banner,
  Box,
  ConfirmCancelDialog,
  MuiTable,
  Spacer,
  TableBody,
  TableCell,
  TableRow,
  Text,
  useToast,
  useTheme,
} from '@color/continuum';
import { ApiClient, ApiError } from '@color/lib';
import { useMutation } from '@tanstack/react-query';
import { groupBy, mapValues } from 'lodash-es';
import { useCallback } from 'react';

import { Candidate, CNVReviewData, ReviewDecision, ReviewDetails } from './types';

const isDecisionPositive = (decision: ReviewDecision) =>
  [ReviewDecision.PRESENT, ReviewDecision.MAYBE].includes(decision);

export const preSubmitReviewWarnings = (block: [Candidate, ReviewDecision][]): string[] => {
  const warnings: string[] = [];

  if (
    block.some(([{ algorithm, does_overlap_pms2_12_15 }, decision]) => {
      return isDecisionPositive(decision) && algorithm === 'cnvkit' && does_overlap_pms2_12_15;
    })
  ) {
    warnings.push(
      'CNVkit call with boundary outside PMS2 segmental duplication will not generate PMS2CL variant and confirmation'
    );
  }

  if (
    block.some(([{ region_name }, decision]) => {
      return isDecisionPositive(decision) && region_name.split(',').length > 1;
    })
  ) {
    warnings.push(
      'One call will generate a single variant based on the first (alphabetically sorted) gene name in its list'
    );
  }

  const positiveDecisionsPerGene = block.reduce(
    (acc, [candidate, decision]) => {
      if (!isDecisionPositive(decision)) {
        return acc;
      }
      const gene = candidate.region_name;
      if (gene in acc) {
        return { ...acc, [gene]: acc[gene] + 1 };
      }
      return { ...acc, [gene]: 1 };
    },
    {} as Record<string, number>
  );

  if (Object.values(positiveDecisionsPerGene).some((n) => n > 1)) {
    warnings.push(
      'Generally structural variants are reported based on a single approved CNV candidate, typically the call best matching the genomic change and driving the classification'
    );
  }

  return warnings;
};

const ReviewDecisionStat = ({ decision, count }: { decision: ReviewDecision; count: number }) => {
  const theme = useTheme();
  const color =
    decision === ReviewDecision.PRESENT ? theme.palette.error.main : theme.palette.text.primary;

  return (
    <TableRow>
      <TableCell>
        <Text sx={{ fontWeight: 'bold', color }}>Marked as {decision}</Text>
      </TableCell>
      <TableCell>
        <Text sx={{ color }}>{count}</Text>
      </TableCell>
    </TableRow>
  );
};

export const SubmitReviewDialogue = ({
  candidates,
  updates,
  onClose,
  onLock,
  onUnlock,
  onServerStateChange,
}: {
  candidates: Candidate[];
  updates: Record<number, ReviewDetails>;
  onClose: () => void;
  onLock: () => void;
  onUnlock: () => void;
  onServerStateChange: () => void;
}) => {
  const { showToast } = useToast();

  const first = candidates[0];
  const { batch_label } = first.sample_sequence_run;
  const { kit_barcode } = first.sample_sequence_run.sample;
  const { chrom } = first;

  const reviewSubmitMutation = useMutation({
    mutationFn: async (data: CNVReviewData[]) => {
      return ApiClient.post('/api/v1/cnv_candidate_blocks/review', { json: data });
    },
  });

  const reviewAccepted = useCallback(() => {
    const data = Object.entries(updates).map(
      ([id, attrs]) => ({ id: parseInt(id, 10), ...attrs }) as CNVReviewData
    );
    onLock();
    reviewSubmitMutation.mutate(data, {
      onSuccess: () => {
        onServerStateChange();
        onClose();
      },
      onError: (error: ApiError) => {
        showToast(error.message, { severity: 'error', variant: 'filled' });
      },
      onSettled: () => onUnlock(),
    });
  }, [updates, onLock, onServerStateChange, onUnlock, onClose, reviewSubmitMutation, showToast]);

  const decisionFrequencies = Object.entries(
    mapValues(groupBy(updates, 'review_decision'), (v) => v.length)
  ).sort((a, b) => a[0].localeCompare(b[0])) as [ReviewDecision, number][];

  const warnings = preSubmitReviewWarnings(
    candidates.map((candidate) => [candidate, updates[candidate.id]!.review_decision!])
  );

  return (
    <ConfirmCancelDialog
      title="Review changes"
      onClose={onClose}
      onCancel={onClose}
      onAccept={reviewAccepted}
      dialogIsOpen
    >
      <Box
        sx={{
          width: '550px',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {warnings.map((warning) => (
            <Banner severity="warning" variant="in-component" key={warning}>
              {warning}
            </Banner>
          ))}
        </Box>
        <Spacer variant="small" />
        <MuiTable size="small">
          <TableBody>
            <TableRow>
              <TableCell>
                <Text style={{ fontWeight: 'bold' }}>Batch name</Text>
              </TableCell>
              <TableCell>
                <Text>{batch_label}</Text>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <Text style={{ fontWeight: 'bold' }}>Kit barcode</Text>
              </TableCell>
              <TableCell>
                <Text>{kit_barcode}</Text>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <Text style={{ fontWeight: 'bold' }}>Chrom</Text>
              </TableCell>
              <TableCell>
                <Text>{chrom}</Text>
              </TableCell>
            </TableRow>
            {decisionFrequencies.map(([decision, count]) => (
              <ReviewDecisionStat key={decision} decision={decision} count={count} />
            ))}
          </TableBody>
        </MuiTable>
      </Box>
    </ConfirmCancelDialog>
  );
};
